import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, User, Building2, Store, Mail } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/landing/Hero";
import { PanAfrican } from "@/components/landing/PanAfrican";
import { SeoAbout } from "@/components/landing/SeoAbout";
import { DownloadCTA } from "@/components/landing/DownloadCTA";
import { MarketFriezeMini } from "@/components/landing/MarketFrieze";

const EXPLORE = [
  {
    to: "/pourquoi-zafara",
    icon: Sparkles,
    label: "Pourquoi Zafara ?",
    text: "L'histoire du nom, la vision et la promesse derrière la marque.",
    color: "#0E7A3B",
  },
  {
    to: "/application",
    icon: Store,
    label: "L'application",
    text: "Ce que Zafara change pour les commerces, vendeurs et livreurs.",
    color: "#F5A800",
  },
  {
    to: "/fondateur",
    icon: User,
    label: "Le fondateur",
    text: "Rencontrez Wilson Eric, l'ingénieur béninois derrière Zafara.",
    color: "#0E7A3B",
  },
  {
    to: "/whilsco-digit",
    icon: Building2,
    label: "Whilsco-Digit",
    text: "Le studio produit qui édite Zafara depuis l'Île Maurice et le Bénin.",
    color: "#F5A800",
  },
];

export default function Home() {
  return (
    <>
      <SEO path="/" />
      <Hero />
      <PanAfrican />
      <SeoAbout />

      {/* Explorer section */}
      <section
        id="explorer"
        data-testid="home-explorer"
        className="relative py-24 md:py-32 bg-white border-y border-[#EDE7D9]"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#0E7A3B]">
              Explorer le site
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#16241B] leading-[1.05] tracking-tighter">
              Chaque sujet a sa page.
              <br />
              <span className="text-[#0E7A3B]">Prenez le temps.</span>
            </h2>
            <MarketFriezeMini className="mt-6 max-w-[220px]" />
            <p className="mt-6 text-lg text-[#16241B]/70 leading-relaxed max-w-2xl">
              Le site est organisé en pages indépendantes — vous choisissez ce
              qui vous intéresse.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {EXPLORE.map((e) => (
              <Link
                key={e.to}
                to={e.to}
                data-testid={`explore-${e.to.slice(1)}`}
                className="group relative overflow-hidden rounded-3xl border border-[#EDE7D9] bg-[#FBFAF6] p-8 md:p-10 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(14,122,59,0.08)] transition-all duration-500"
              >
                <div className="flex items-start justify-between gap-6">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-2xl"
                    style={{ background: `${e.color}15`, border: `1px solid ${e.color}30` }}
                  >
                    <e.icon className="h-6 w-6" style={{ color: e.color }} />
                  </div>
                  <ArrowRight
                    className="h-5 w-5 text-[#16241B]/30 group-hover:text-[#0E7A3B] group-hover:translate-x-1 transition-all"
                  />
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl text-[#16241B] tracking-tight">
                  {e.label}
                </h3>
                <p className="mt-3 text-[#16241B]/70 text-base leading-relaxed">
                  {e.text}
                </p>
              </Link>
            ))}
          </div>

          <div className="mt-10 rounded-3xl bg-[#16241B] text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 noise-overlay relative overflow-hidden">
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-[#F5A800]/15 blur-3xl" />
            <div className="relative">
              <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#F5A800]">
                Une question ?
              </p>
              <p className="mt-2 font-display text-2xl md:text-3xl leading-tight tracking-tight max-w-xl">
                Écrivez-nous ou passez par WhatsApp — on répond vite.
              </p>
            </div>
            <Link
              to="/contact"
              className="relative inline-flex items-center gap-2 rounded-full bg-[#F5A800] text-[#16241B] px-6 py-3 text-sm font-semibold hover:bg-white transition-colors"
            >
              <Mail className="h-4 w-4" />
              Aller à la page Contact
            </Link>
          </div>
        </div>
      </section>

      <DownloadCTA />
    </>
  );
}
